(function() {

// === 設定 ===
let DEBUG_MODE = true;
let log = DEBUG_MODE ? console.log : () => {};

const SPIXN_BASE_COLOR = '#542D61';
const PRICE_CONTAINER_SELECTOR = '.product-page--right-column .product-price--root';
const VARIANT_INPUT_SELECTOR = '.product-page--root product-buy-buttons-root select[name="id"]';
const ALTERNATIVE_VARIANT_INPUT_SELECTOR = '.featured-product--root product-buy-buttons-root select[name="id"]';
const SHOW_BUYBACK_PRICE_INFO = true; // バイバック保証の価格情報表示ON/OFF（true: 表示する, false: 表示しない）

// === API関連の設定 ===
let API_BASE_URL = 'https://ieyxoq7t4c.execute-api.ap-northeast-1.amazonaws.com/dev';
const API_TIMEOUT = 10000;
const DEFAULT_HEADERS = {
	'Content-Type': 'application/json',
	'Accept': 'application/json'
};

const SPIXN_CONFIG = {
	USE_API: true,
	ENABLE_CACHE: true,
	CACHE_TTL: 5 * 60 * 1000,
	MAX_RETRIES: 3,
	RETRY_DELAY: 1000
};

let SHOP_ID = null;
const buybackCache = new Map();

function getShopId() { return SHOP_ID; }
function setShopId(shopId) { SHOP_ID = shopId; }

function validateShopId(shopId) {
	if (!shopId || typeof shopId !== 'string') return false;
	const shopifyDomainPattern = /^[a-zA-Z0-9\-]+\.myshopify\.com$/;
	return shopifyDomainPattern.test(shopId);
}

function convertToShopifyGID(id, type) {
	if (!id) return null;
	return `gid://shopify/${type}/${id.toString()}`;
}

async function callSpixnAPI(endpoint, params = {}, options = {}) {
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT);

	try {
		const url = new URL(`${API_BASE_URL}${endpoint}`);
		
		Object.keys(params).forEach(key => {
			if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
				url.searchParams.append(key, params[key]);
			}
		});

		log('[SPIXN V5] API call:', url.toString());

		const response = await fetch(url.toString(), {
			method: 'GET',
			headers: { ...DEFAULT_HEADERS, ...options.headers },
			signal: controller.signal
		});

		clearTimeout(timeoutId);

		if (!response.ok) {
			throw new Error(`API call failed: ${response.status} ${response.statusText}`);
		}

		const data = await response.json();
		log('[SPIXN V5] API response:', data);
		
		return data;
	} catch (error) {
		clearTimeout(timeoutId);
		if (error.name === 'AbortError') {
			throw new Error('API call timeout');
		}
		console.error('[SPIXN V5] API call error:', error);
		throw error;
	}
}

async function callSpixnAPIWithRetry(endpoint, params = {}, options = {}) {
	const maxRetries = options.maxRetries || SPIXN_CONFIG.MAX_RETRIES;
	const retryDelay = options.retryDelay || SPIXN_CONFIG.RETRY_DELAY;

	for (let attempt = 1; attempt <= maxRetries; attempt++) {
		try {
			return await callSpixnAPI(endpoint, params, options);
		} catch (error) {
			if (attempt === maxRetries) throw error;
			log(`[SPIXN V5] API call failed (attempt ${attempt}/${maxRetries}), retrying...`);
			await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
		}
	}
}

async function getBuybackInformation(variantId) {
	try {
		const shopId = getShopId();
		if (!shopId) {
			log('[SPIXN V5] Shop ID not set');
			return null;
		}

		const cacheKey = `${shopId}_${variantId}`;
		if (SPIXN_CONFIG.ENABLE_CACHE && buybackCache.has(cacheKey)) {
			const cached = buybackCache.get(cacheKey);
			if (Date.now() - cached.timestamp < SPIXN_CONFIG.CACHE_TTL) {
				log('[SPIXN V5] Using cached result for variant:', variantId);
				return cached.data;
			}
		}

		const params = {
			sp_shop_id: shopId,
			sp_variant_id: convertToShopifyGID(variantId, 'ProductVariant')
		};

		const response = await callSpixnAPIWithRetry('/shopify/buyback', params);

		if (!response.data || !Array.isArray(response.data) || response.data.length === 0) {
			log('[SPIXN V5] No buyback information found for variant:', variantId);
			if (SPIXN_CONFIG.ENABLE_CACHE) {
				buybackCache.set(cacheKey, { data: null, timestamp: Date.now() });
			}
			return null;
		}

		const buybackData = response.data[0];
		
		const transformedData = {
			buyback_variant_id: parseInt(buybackData.buyback_sp_variant_id),
			insurance_fee: buybackData?.insurance_fee || 0,
			insurance_period_days: buybackData.insurance_period_days,
			a_price: buybackData.a_price,
			b_price: buybackData.b_price,
			c_price: buybackData.c_price
		};

		if (SPIXN_CONFIG.ENABLE_CACHE) {
			buybackCache.set(cacheKey, { data: transformedData, timestamp: Date.now() });
		}

		return transformedData;
	} catch (error) {
		console.error('[SPIXN V5] Failed to get buyback information for variant', variantId, ':', error);
		return null;
	}
}

async function initializeSpixnAPI(shopId) {
	try {
		log('[SPIXN V5] Initializing SPIXN API...');
		
		if (shopId && validateShopId(shopId)) {
			setShopId(shopId);
			log('[SPIXN V5] Shop ID set:', shopId);
			log('[SPIXN V5] SPIXN API initialization completed');
			return true;
		} else {
			console.warn('[SPIXN V5] Shop ID not available or invalid:', shopId);
			return false;
		}
	} catch (error) {
		console.error('[SPIXN V5] SPIXN API initialization failed:', error);
		return false;
	}
}

function getCurrentVariantId() {
	const variantInput = document.querySelector(VARIANT_INPUT_SELECTOR) || document.querySelector(ALTERNATIVE_VARIANT_INPUT_SELECTOR);
	return variantInput ? parseInt(variantInput.value, 10) : null;
}

function showBuybackNotice(buybackInfo) {
	const priceContainer = document.querySelector(PRICE_CONTAINER_SELECTOR);
	if (!priceContainer) {
		log('[SPIXN V5] Price container not found');
		return;
	}

	// 既存のメッセージを削除
	const existingNotice = document.querySelector('#spixn-buyback-notice-v5');
	if (existingNotice) {
		existingNotice.remove();
	}

	// バイバック保証情報から価格と手数料を取得
	const maxPrice = buybackInfo.a_price.toLocaleString();
	const insuranceFee = buybackInfo.insurance_fee.toLocaleString();
	// SHOW_BUYBACK_PRICE_INFOの設定に応じて表示内容を切り替え
	const priceInfoLine = SHOW_BUYBACK_PRICE_INFO 
		? `手数料<span style="font-size:0.65rem;">¥</span>${insuranceFee}で 最大<span style="font-size:0.65rem;">¥</span>${maxPrice}のバイバック保証<br />`
		: '';


	const notice = document.createElement('div');
	notice.id = 'spixn-buyback-notice-v5';
	notice.style.cssText = `
		font-size: 0.72rem;
		color: ${SPIXN_BASE_COLOR};
		margin-top: 0.5rem;
		line-height: 1.4;
	`;
	notice.innerHTML = `
		こちらの商品は<svg width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg" style="vertical-align: text-top;">
		<g clip-path="url(#clip0_1427_3087)">
		<path d="M7.00123 13.0025C10.5918 13.0025 13.5025 10.0918 13.5025 6.50123C13.5025 2.9107 10.5918 0 7.00123 0C3.4107 0 0.5 2.9107 0.5 6.50123C0.5 10.0918 3.4107 13.0025 7.00123 13.0025Z" fill="#1AB298"/>
		<path d="M3.96566 9.09757C4.42183 9.55602 5.04143 9.81448 5.68816 9.8161H5.71277C6.06158 9.80932 6.40539 9.73191 6.72344 9.58853C7.04149 9.44516 7.32717 9.2388 7.56323 8.98192L7.60753 8.9327L7.87575 8.63496L9.59825 9.83579H11.3774L9.04951 8.21171L5.78905 5.92816L5.01146 5.3868C4.9124 5.31079 4.83161 5.21357 4.77501 5.10227C4.71841 4.99097 4.68744 4.86841 4.68437 4.74359C4.6813 4.61876 4.70622 4.49483 4.75728 4.38088C4.80834 4.26694 4.88426 4.16587 4.97947 4.08508C5.19671 3.89346 5.47725 3.78914 5.76691 3.79226C6.36486 3.79226 6.85209 4.20812 6.85455 4.71995C6.85491 4.77537 6.84913 4.83067 6.83732 4.88482L6.81763 4.9611C6.79964 5.01287 6.77742 5.06307 6.7512 5.1112C6.71941 5.16848 6.68146 5.22212 6.638 5.27114L6.48298 5.45324L7.32209 6.0389L7.41067 5.93801C7.49933 5.83491 7.57687 5.72273 7.64198 5.60334C7.70324 5.4932 7.75273 5.3769 7.78962 5.25638L7.82161 5.14565V5.11858C7.85105 4.98608 7.8659 4.85076 7.8659 4.71503C7.8659 3.64215 6.91606 2.77351 5.75952 2.77351C5.21493 2.76899 4.68831 2.96823 4.28309 3.3321C4.08469 3.50684 3.92586 3.7219 3.81722 3.96293C3.70859 4.20396 3.65265 4.46541 3.65315 4.72979C3.65661 5.01058 3.7227 5.28706 3.84658 5.53908C3.97046 5.79109 4.14901 6.01228 4.36922 6.18653L4.38398 6.19884L7.01941 8.037L6.8324 8.24617C6.68824 8.41205 6.51126 8.54624 6.31262 8.64027C6.11398 8.73429 5.898 8.78611 5.67832 8.79245C5.30467 8.79231 4.94597 8.64565 4.67926 8.38397C4.48976 8.18669 4.38319 7.92421 4.38152 7.65067V7.11177H3.36524V7.64082C3.36468 7.91118 3.41743 8.179 3.52045 8.42896C3.62348 8.67892 3.77476 8.90612 3.96566 9.09757Z" fill="white"/>
		<path d="M9.25796 5.54199L8.24414 6.68131L9.08325 7.26942L10.6212 5.54199H9.25796Z" fill="white"/>
		<path d="M10.5423 4.91367C10.9065 4.91367 11.2018 4.61842 11.2018 4.2542C11.2018 3.88998 10.9065 3.59473 10.5423 3.59473C10.1781 3.59473 9.88281 3.88998 9.88281 4.2542C9.88281 4.61842 10.1781 4.91367 10.5423 4.91367Z" fill="white"/>
		</g>
		<defs>
		<clipPath id="clip0_1427_3087">
		<rect width="13" height="13" fill="white" transform="translate(0.5)"/>
		</clipPath>
		</defs>
		</svg>バイバック保証対象商品です。<br />
		${priceInfoLine}
		カートのページでバイバック保証を付与できます。
	`;

	priceContainer.insertAdjacentElement('afterend', notice);
	log('[SPIXN V5] Buyback notice displayed');
}

function hideBuybackNotice() {
	const existingNotice = document.querySelector('#spixn-buyback-notice-v5');
	if (existingNotice) {
		existingNotice.remove();
		log('[SPIXN V5] Buyback notice hidden');
	}
}

async function checkAndDisplayNotice() {
	const currentVariantId = getCurrentVariantId();
	if (!currentVariantId) {
		log('[SPIXN V5] Current variant ID not found');
		hideBuybackNotice();
		return;
	}

	log('[SPIXN V5] Checking buyback info for variant:', currentVariantId);
	const buybackInfo = await getBuybackInformation(currentVariantId);

	if (buybackInfo) {
		log('[SPIXN V5] Buyback information found, showing notice');
		showBuybackNotice(buybackInfo);
	} else {
		log('[SPIXN V5] No buyback information, hiding notice');
		hideBuybackNotice();
	}
}

function setupVariantChangeObserver() {
	log('[SPIXN V5] Setting up variant change observer');
	
	const variantInput = document.querySelector(VARIANT_INPUT_SELECTOR) || document.querySelector(ALTERNATIVE_VARIANT_INPUT_SELECTOR);
	
	if (!variantInput) {
		log('[SPIXN V5] Variant input not found');
		return;
	}

	let lastValue = variantInput.value;

	// ポーリングで値の変更を検知
	const intervalId = setInterval(() => {
		if (variantInput.value !== lastValue) {
			log('[SPIXN V5] Variant change detected:', lastValue, '->', variantInput.value);
			lastValue = variantInput.value;
			checkAndDisplayNotice();
		}
	}, 500);
	
	// インターバルIDを保存
	window.spixnV5VariantInterval = intervalId;

	log('[SPIXN V5] Variant change observer setup complete');
}

function checkZenlinkElement() {
	return document.getElementById("zenlink5F8304A2-root") !== null;
}

function disableSpixnFeatures() {
	log('[SPIXN V5] Disabling SPIXN features due to zenlink detection');
	
	// UIを非表示
	hideBuybackNotice();
	
	// インターバルを停止
	if (window.spixnV5VariantInterval) {
		clearInterval(window.spixnV5VariantInterval);
		window.spixnV5VariantInterval = null;
	}
	
	// オブザーバーを停止
	if (window.spixnV5ZenlinkObserver) {
		window.spixnV5ZenlinkObserver.disconnect();
		window.spixnV5ZenlinkObserver = null;
	}
	
	log('[SPIXN V5] SPIXN features disabled');
}

function setupZenlinkObserver() {
	log('[SPIXN V5] Setting up zenlink observer');
	
	// 初期チェック
	if (checkZenlinkElement()) {
		log('[SPIXN V5] zenlink element detected on initial check');
		disableSpixnFeatures();
		return;
	}
	
	// 動的監視の設定
	const observer = new MutationObserver(() => {
		if (checkZenlinkElement()) {
			log('[SPIXN V5] zenlink element detected during observation');
			disableSpixnFeatures();
		}
	});
	
	observer.observe(document.body, {
		childList: true,
		subtree: true
	});
	
	window.spixnV5ZenlinkObserver = observer;
	log('[SPIXN V5] zenlink observer setup complete');
}

async function initialize(shopId, apiBaseUrl, options = {}) {
	log('🚀 [SPIXN V5] Shopify Product Buyback Notice initialized');
	
	// zenlink要素の存在チェック（初期化を停止する条件）
	if (checkZenlinkElement()) {
		log('🛑 [SPIXN V5] zenlink element detected, skipping initialization');
		return;
	}
	
	if (typeof options.debugMode !== 'undefined') {
		DEBUG_MODE = options.debugMode;
		log = DEBUG_MODE ? console.log : () => {};
	}
	
	if (apiBaseUrl) {
		API_BASE_URL = apiBaseUrl;
		log('[SPIXN V5] API Base URL set:', apiBaseUrl);
	}
	
	const apiInitialized = await initializeSpixnAPI(shopId);
	if (!apiInitialized) {
		console.warn('[SPIXN V5] API initialization failed, skipping setup');
		return;
	}
	
	// 初回チェック
	await checkAndDisplayNotice();
	
	// バリアント変更を監視
	setupVariantChangeObserver();
	
	// zenlink要素の動的監視を開始
	setupZenlinkObserver();
	
	log('✅ [SPIXN V5] Initialization complete');
}

window.ShopifyBuybackProductV5 = {
	initialize,
	getBuybackInformation,
	getShopId,
	setShopId
};

})();
